import { useEffect, useMemo, useState } from 'react';
import { useEdges, useNodes } from '@xyflow/react';
import { Input } from '@/components/ui/input';
import type { FlowEdgeData, FlowNodeData } from '@/types/flow';

export interface WeightsState {
    // null = todas as conexões dividem igual (nenhum campo preenchido)
    weights: { transition_id: number; weight: number | null }[];
    valid: boolean;
    changed: boolean;
}

interface RandomWeightsEditorProps {
    nodeId: string;
    onChange: (state: WeightsState) => void;
}

// Porcentagem de sorteio de cada node conectado ao gatilho. Tudo vazio = chance igual.
export default function RandomWeightsEditor({ nodeId, onChange }: RandomWeightsEditorProps) {
    const edges = useEdges();
    const nodes = useNodes();

    const conexoes = useMemo(
        () => edges
            .filter((e) => e.source === nodeId && (e.data as FlowEdgeData | undefined)?.id
                && !e.sourceHandle?.startsWith('button-'))
            .map((e) => {
                const data = e.data as FlowEdgeData;
                const alvo = nodes.find((n) => n.id === e.target);
                return {
                    transitionId: data.id as number,
                    nome: (alvo?.data as FlowNodeData | undefined)?.node_name || `Node #${e.target}`,
                    pesoSalvo: data.weight ?? null,
                };
            }),
        [edges, nodes, nodeId],
    );

    const [valores, setValores] = useState<Record<number, string>>({});

    useEffect(() => {
        setValores(Object.fromEntries(
            conexoes.map((c) => [c.transitionId, c.pesoSalvo == null ? '' : String(c.pesoSalvo)]),
        ));
    }, [conexoes]);

    const preenchidos = conexoes.filter((c) => (valores[c.transitionId] ?? '') !== '');
    const soma = preenchidos.reduce((s, c) => s + Number(valores[c.transitionId]), 0);
    const modoIgual = preenchidos.length === 0;
    const valid = modoIgual || (preenchidos.length === conexoes.length && soma === 100);
    const changed = conexoes.some((c) => (c.pesoSalvo == null ? '' : String(c.pesoSalvo)) !== (valores[c.transitionId] ?? ''));

    useEffect(() => {
        onChange({
            weights: conexoes.map((c) => ({
                transition_id: c.transitionId,
                weight: modoIgual ? null : Number(valores[c.transitionId]),
            })),
            valid,
            changed,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valores, conexoes]);

    if (conexoes.length < 2) return null;

    const igual = Math.round(100 / conexoes.length);

    return (
        <div className="space-y-2 rounded-lg border border-orange-200 bg-orange-50/50 p-3">
            <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Chance de cada node</p>
                <span className={`text-xs font-medium ${modoIgual ? 'text-gray-500' : valid ? 'text-green-600' : 'text-red-600'}`}>
                    {modoIgual ? 'Dividido igual' : `Soma: ${soma}%`}
                </span>
            </div>
            {conexoes.map((c) => (
                <div key={c.transitionId} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm text-gray-700" title={c.nome}>{c.nome}</span>
                    <div className="relative w-20">
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            inputMode="numeric"
                            value={valores[c.transitionId] ?? ''}
                            placeholder={String(igual)}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                                setValores((prev) => ({ ...prev, [c.transitionId]: v === '' ? '' : String(Math.min(100, Number(v))) }));
                            }}
                            className="h-8 pr-6 text-right"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                    </div>
                </div>
            ))}
            <p className="text-xs text-gray-500">
                {modoIgual
                    ? 'Deixe em branco para todos terem a mesma chance, ou preencha todos somando 100%.'
                    : valid
                        ? 'Cada lead que entrar sorteia um node com essas chances.'
                        : preenchidos.length < conexoes.length
                            ? 'Preencha todos os nodes, ou apague todos para dividir igual.'
                            : soma < 100
                                ? `Faltam ${100 - soma}% para fechar 100%.`
                                : `Passou ${soma - 100}% de 100%.`}
            </p>
        </div>
    );
}
